/**
 * Stable File URL — the stable, non-expiring link to a file stored behind the
 * s3-adapter. Resolving it (s3-adapter GET with `?nofollow=true`) yields a
 * short-lived Presigned URL for the actual bytes.
 *
 * Path shape, with all four s3-adapter mount aliases:
 *
 *   /{api|eclaim|etask|eflow}/v1/bundles/{bundleId}/{key}
 *
 * The host is deliberately NOT validated or trusted: consumers must never
 * fetch the host stored in task data. Resolve calls rebase `bundleId`/`key`
 * onto their own configured s3-adapter base URL (SSRF guard + environment
 * portability).
 */
const STABLE_FILE_URL_PATH = /^\/(?:api|eclaim|etask|eflow)\/v1\/bundles\/([^/]+)\/(.+)$/;
// `new URL()` already collapses literal `.`/`..` path segments, but encoded
// variants survive normalization and would only be decoded by the upstream
// server: a segment can smuggle a path separator (`..%2Fadmin` → `../admin`)
// or hide one behind double encoding (`%252e%252e%252f` → `%2e%2e%2f` → `../`).
// Decode towards a fixpoint and reject dot segments and separators at every depth.
const MAX_DECODE_ITERATIONS = 3;
function isUnsafeSegment(segment) {
    let current = segment;
    for (let i = 0; i < MAX_DECODE_ITERATIONS; i++) {
        let decoded;
        try {
            decoded = decodeURIComponent(current);
        }
        catch {
            // First pass: the URL itself carries malformed percent-encoding.
            // Later passes: the decoded content merely contains a literal `%`
            // (e.g. "50%_rabatt"), which is not an encoding — nothing left to decode.
            return i === 0;
        }
        if (decoded === "." || decoded === ".." || decoded.includes("/") || decoded.includes("\\")) {
            return true;
        }
        if (decoded === current) {
            return false; // fixpoint reached without finding anything unsafe
        }
        current = decoded;
    }
    return true; // still decoding after the cap — suspiciously deep nesting
}
function isS3Hostname(hostname) {
    return (hostname === "s3.amazonaws.com" ||
        hostname.endsWith(".s3.amazonaws.com") ||
        /^s3[.-][a-z0-9-]+\.amazonaws\.com$/.test(hostname) ||
        /\.s3[.-][a-z0-9-]+\.amazonaws\.com$/.test(hostname));
}
/**
 * Parses a Stable File URL into its `bundleId` and `key` path parts.
 * Returns `null` for anything that is not a Stable File URL — including
 * presigned S3 URLs, non-http(s) schemes, and path-traversal attempts.
 */
export function parseStableFileUrl(url) {
    let parsed;
    try {
        parsed = new URL(url);
    }
    catch {
        return null;
    }
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
        return null;
    }
    const match = STABLE_FILE_URL_PATH.exec(parsed.pathname);
    if (!match) {
        return null;
    }
    const [, bundleId, key] = match;
    if (!bundleId || !key) {
        return null;
    }
    if (isUnsafeSegment(bundleId) || key.split("/").some(isUnsafeSegment)) {
        return null;
    }
    return { bundleId, key };
}
/** Convenience predicate for zod refinements. */
export function isStableFileUrl(url) {
    return parseStableFileUrl(url) !== null;
}
/**
 * Validates that a resolve response body is a Presigned URL on S3 and
 * normalizes it to HTTPS.
 *
 * TODO: Remove the following info once s3-adapter is fixed and this becomes stale.
 * The s3-adapter is known to report errors with status 200 and to serve
 * gateway HTML pages on misrouting, so the body is only trusted if it parses
 * as a URL on an S3 host. The presign signature is computed over host, path,
 * headers, and query — not the scheme — so upgrading `http:` to `https:`
 * keeps the signature valid while avoiding mixed-content issues.
 *
 * Returns the normalized URL, or null if the body is not a Presigned URL.
 */
export function normalizePresignedS3Url(url) {
    let parsedUrl;
    try {
        parsedUrl = new URL(url);
    }
    catch {
        return null;
    }
    if ((parsedUrl.protocol !== "http:" && parsedUrl.protocol !== "https:") || !isS3Hostname(parsedUrl.hostname)) {
        return null;
    }
    parsedUrl.protocol = "https:";
    return parsedUrl.toString();
}
